import { resolveStepIdFromNodeId } from "./builder";
import { EVENT_NODE_ID_PREFIX, buildEventNodeId, isEventNodeId, parseEventNodeId, FLOW_EVENT_NODE_TYPE, isEndNodeId, isGatewayNodeId, parseGatewayNodeId, OBJECT_ID_RE } from "./constants";
export const ASSIGNMENT_NODE_ID_PREFIX = "assignment-";
export { EVENT_NODE_ID_PREFIX, FLOW_EVENT_NODE_TYPE, buildEventNodeId, isEventNodeId, parseEventNodeId, isEndNodeId };
function classifyNodeEnd(nodeId, ctx) {
    // Raw-id event nodes (ADR 0003): a start event classifies as 'start' so
    // start-edge semantics (capabilities, insert/fork routing) apply, but it
    // carries its eventId — actions attach/detach through the events endpoints.
    const eventKind = ctx?.eventKindByNodeId?.get(nodeId);
    if (eventKind) {
        return {
            kind: eventKind === "start" ? "start" : "event",
            nodeId,
            eventId: nodeId
        };
    }
    if (isEndNodeId(nodeId)) {
        return {
            kind: "end",
            nodeId
        };
    }
    if (isGatewayNodeId(nodeId)) {
        // Composite `gateway-<stepId>-rule-<ruleId>` ids collapse to the owning
        // stepId so task-fork role detection works uniformly for both shapes.
        // `parseGatewayNodeId` is non-null here because `isGatewayNodeId` passed.
        const parsed = parseGatewayNodeId(nodeId);
        return {
            kind: "gateway",
            nodeId,
            stepId: parsed.stepId
        };
    }
    // VESTIGIAL: no live mint site produces `assignment-`-prefixed node ids
    // anymore — placeholders render under their bare `stableStepId` (and carry
    // `canvasKind: 'assignment'`, see nodeKind.ts). Behavior kept for saved
    // layouts/ids that may still hold the prefix; removal is a follow-up.
    if (nodeId.startsWith(ASSIGNMENT_NODE_ID_PREFIX)) {
        return {
            kind: "assignment",
            nodeId,
            assignmentId: nodeId.slice(ASSIGNMENT_NODE_ID_PREFIX.length)
        };
    }
    if (isEventNodeId(nodeId)) {
        const eventId = parseEventNodeId(nodeId);
        return {
            kind: "event",
            nodeId,
            ...eventId ? {
                eventId
            } : {}
        };
    }
    return {
        kind: "step",
        nodeId,
        stepId: resolveStepIdFromNodeId(nodeId) ?? nodeId
    };
}
const RULE_HANDLE_PREFIX = "rule-";
export function buildRuleHandleId(ruleId) {
    return `${RULE_HANDLE_PREFIX}${ruleId}`;
}
export function isRuleHandleId(handleId) {
    return !!handleId && handleId.startsWith(RULE_HANDLE_PREFIX);
}
/**
 * Return the raw rule id from a `rule-<id>` handle id. Unlike `extractRuleId`
 * this does not ObjectId-validate — the caller already knows the id space
 * (e.g. a React Flow handle id comes from the builder, not user input).
 */ export function parseRuleHandleId(handleId) {
    if (!isRuleHandleId(handleId)) return undefined;
    return handleId.slice(RULE_HANDLE_PREFIX.length);
}
export function extractRuleId(sourceHandle) {
    const raw = parseRuleHandleId(sourceHandle);
    if (raw === undefined) return undefined;
    // Guard against non-ObjectId rule ids slipping through — callers pass the
    // ruleId into backend mutations (ObjectId-validated). A bad id would 400.
    return OBJECT_ID_RE.test(raw) ? raw : undefined;
}
/**
 * Classify an edge between two nodes. Identifies start/end/step/gateway/
 * assignment ends, extracts the rule id from a decision edge's sourceHandle,
 * and tags task-fork gateway involvement so callers can route semantics
 * through the `+` correctly.
 */ export function classifyEdgeEnds(sourceNodeId, targetNodeId, sourceHandle, ctx) {
    const source = classifyNodeEnd(sourceNodeId, ctx);
    const target = classifyNodeEnd(targetNodeId, ctx);
    // Edges out of a gateway carry no rule handle; a rule-scoped gateway's id
    // names the decision output instead.
    const ruleId = extractRuleId(sourceHandle) ?? (source.kind === "gateway" ? parseGatewayNodeId(sourceNodeId)?.ruleId : undefined);
    // Task-fork role is detectable purely from the ids: a `gateway-X` on one
    // end and `X` on the other means we're looking at the split around the
    // `+`. Rule-scoped `gateway-<X>-rule-<R>` gateways also report stepId `X`
    // from `classifyNodeEnd`, so a decision step's rule edge into its own
    // rule-scoped gateway (ruleId present) is a legitimate beforeFork.
    let taskForkRole;
    if (source.kind === "step" && target.kind === "gateway" && source.stepId != null && source.stepId === target.stepId) {
        taskForkRole = "beforeFork";
    } else if (source.kind === "gateway") {
        taskForkRole = "afterFork";
    }
    return {
        source,
        target,
        ruleId,
        taskForkRole
    };
}

//# sourceMappingURL=edgeClassifier.js.map